import React, { useState, useEffect, useCallback, useRef } from "react";
import * as echarts from "echarts";
import "echarts-wordcloud";
import NavigationSidebar from "../components/Navigationsidebar/NavigationSidebar";
import Header from "../components/Header";
import FilterSidebar from "../components/FilterSidebar/FilterSidebar";
import { FILTER_CONFIG } from "../config/filterconfig";
import rawData from "../current-survey-openText.json";
import "./D17_Open_Text_Top_Themes.css";

const qNumOptions = [
  { value: "MQ0305", label: "What Makes this Company a Great Place to Work" },
  {
    value: "MQ0156",
    label: "What Would Make this Place a Better Place to Work",
  },
  {
    value: "MQ0160",
    label: "What Would you Like to Tell the Senior Leadership Team",
  },
];

const D17_Open_Text_Top_ThemesDashboard = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [filterCriteria, setFilterCriteria] = useState({});
  const [filterOptions, setFilterOptions] = useState({});
  const [selectedQuestion, setSelectedQuestion] = useState(qNumOptions[0].value);
  const chartInstance = useRef(null);
  const containerRef = useRef(null);

  // Generate filter options dynamically based on FILTER_CONFIG
  useEffect(() => {
    const generateFilterOptions = () => {
      const options = {};

      FILTER_CONFIG.forEach(({ fieldName, jsonKey, sortOrder }) => {
        let uniqueValues = [...new Set(rawData.map((item) => item[jsonKey]))];
        uniqueValues = uniqueValues.filter((value) => value !== null);

        if (sortOrder) {
          uniqueValues = uniqueValues.sort(
            (a, b) => sortOrder.indexOf(a) - sortOrder.indexOf(b)
          );
        } else {
          uniqueValues.sort((a, b) => a.localeCompare(b));
        }

        options[fieldName] = uniqueValues;
      });

      setFilterOptions(options);
    };

    generateFilterOptions();
  }, []);

  // Function to render the chart
  const renderChart = useCallback(() => {
    if (!containerRef.current) return;

    // Clean up existing instance if it exists
    if (chartInstance.current) {
      chartInstance.current.dispose();
      chartInstance.current = null;
    }

    // Ensure container size is available and reasonable
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;

    if (containerWidth < 10 || containerHeight < 10) {
      console.warn("Container has invalid dimensions:", containerWidth, containerHeight);
      return;
    }

    // Initialize chart
    chartInstance.current = echarts.init(containerRef.current);

    // Define the CCI colors
    const myCCI = ["#004983", "#0083ca", "#68ae45"];

    // Filter data based on selected question and filter criteria
    const filteredData = rawData.filter(
      (item) => 
        item.q_num === selectedQuestion &&
        Object.keys(filterCriteria).every((key) => {
          const filterValues = filterCriteria[key];
          const itemValue = item[key];
          return (
            !filterValues || 
            filterValues.length === 0 || 
            filterValues.includes(itemValue)
          );
        })
    );

    // Count themes
    const themeCounts = {};
    filteredData.forEach((entry) => {
      // Extract all themes from the entry
      for (let i = 1; i <= 8; i++) {
        const themeKey = `theme_${i.toString().padStart(2, '0')}`;
        const theme = entry[themeKey];
        
        // Only count valid themes
        if (theme && theme.toLowerCase() !== 'na') {
          themeCounts[theme] = (themeCounts[theme] || 0) + 1;
        }
      }
    });

    // Convert to format expected by word cloud
    const wordCloudData = Object.keys(themeCounts).map(theme => ({
      name: theme,
      value: themeCounts[theme]
    }));

    // Sort by frequency, largest first
    wordCloudData.sort((a, b) => b.value - a.value);

    // Create chart options
    const option = {
      backgroundColor: 'white',
      tooltip: {
        show: true,
        formatter: function(params) {
          return params.data.name + ': ' + params.data.value;
        }
      },
      title: {
        text: 'Open Text Question: ' + 
          (qNumOptions.find(q => q.value === selectedQuestion)?.label || ''),
        left: 'center',
        top: 20,
        textStyle: {
          fontSize: 16
        }
      },
      series: [{
        type: 'wordCloud',
        shape: 'circle',
        // Position at the center of the container
        left: '10%',
        top: '15%',
        right: '10%',
        bottom: '10%',
        width: '80%',
        height: '75%',
        // Visual settings
        rotationRange: [-45, 45],
        rotationStep: 5,
        gridSize: 20,
        sizeRange: [14, 50],
        // IMPORTANT: Set to false to prevent words from disappearing
        drawOutOfBound: false,
        textStyle: {
          fontFamily: 'Arial, sans-serif',
          fontWeight: 'bold',
          color: function() {
            return myCCI[Math.floor(Math.random() * myCCI.length)];
          }
        },
        emphasis: {
          focus: 'self',
          textStyle: {
            shadowBlur: 10,
            shadowColor: '#333'
          }
        },
        data: wordCloudData.slice(0, 100) // Limit to 100 words for performance
      }]
    };

    // Set the options and render the chart
    chartInstance.current.setOption(option);
  }, [selectedQuestion, filterCriteria]);

  // Initialize chart when component mounts or when filtering changes
  useEffect(() => {
    // Initial render after a small delay to ensure container is available
    const timer = setTimeout(() => {
      renderChart();
    }, 100);

    // Handle window resize
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };
    window.addEventListener('resize', handleResize);

    // Clean up function
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, [renderChart]);

  // Update chart when sidebar visibility changes
  useEffect(() => {
    // Add delay to wait for sidebar animation
    const timer = setTimeout(() => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [isSidebarOpen, isFilterSidebarOpen]);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen(prev => !prev);
  }, []);

  const toggleFilterSidebar = useCallback(() => {
    setIsFilterSidebarOpen(prev => !prev);
  }, []);

  const handleQuestionChange = (event) => {
    setSelectedQuestion(event.target.value);
  };

  const handleFilterChange = (event, fieldName) => {
    setFilterCriteria(prev => ({
      ...prev,
      [fieldName]: event.target.value ? [event.target.value] : []
    }));
  };

  const refreshChart = () => {
    // Force a complete re-render of the chart
    renderChart();
  };

  return (
    <div className="D17_Open_Text_Top_Themes-dashboard-container">
      <Header
        title="Engagement Questions Word Cloud"
        toggleSidebar={toggleSidebar}
        toggleFilterSidebar={toggleFilterSidebar}
        refreshPage={refreshChart}
      />
      <NavigationSidebar
        className={
          isSidebarOpen
            ? "D17_Open_Text_Top_Themes-sidebar-open"
            : "D17_Open_Text_Top_Themes-sidebar-closed"
        }
        isOpen={isSidebarOpen}
      />
      <div className="D17_Open_Text_Top_Themes-main-content">
        <div className="D17_Open_Text_Top_Themes-chart-container">
          <div 
            ref={containerRef}
            id="main" 
            className="D17_Open_Text_Top_Themes-chart"
          ></div>
        </div>
        <div className="D17_Open_Text_Top_Themes-filters-container">
          <div className="question-selector">
            <label htmlFor="question-dropdown">Select Question:</label>
            <select
              id="question-dropdown"
              value={selectedQuestion}
              onChange={handleQuestionChange}
            >
              {qNumOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {FILTER_CONFIG.map(({ fieldName, displayName }) => (
            <div key={fieldName} className={`${fieldName}-selector`}>
              <label htmlFor={`${fieldName}-dropdown`}>
                Select {displayName}:
              </label>
              <select
                id={`${fieldName}-dropdown`}
                value={filterCriteria[fieldName]?.[0] || ""}
                onChange={(event) => handleFilterChange(event, fieldName)}
              >
                <option value="">All {displayName}</option>
                {filterOptions[fieldName]?.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          ))}
          
          <button 
            onClick={refreshChart}
            className="D17_Open_Text_Top_Themes-refresh-button"
          >
            Refresh Word Cloud
          </button>
        </div>
      </div>
    </div>
  );
};

export default D17_Open_Text_Top_ThemesDashboard;